<template>
  <div class="classifications-settings">
    <div class="d-flex justify-space-between align-center flex-wrap my-4">
      <h3>{{ listStatus === 'deleted' ? 'التصنيفات المحذوفة' : 'جميع تصنيفات الطلب' }}</h3>
      <v-btn v-if="listStatus === 'active'" color="primary" @click="openAddDialog">
        <v-icon left>mdi-plus</v-icon>
        إضافة تصنيف
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
      <span class="filter-label">الحالة:</span>
      <v-btn-toggle
        v-model="listStatus"
        mandatory
        dense
        color="primary"
        @change="onListStatusChange"
      >
        <v-btn value="active" small>التصنيفات النشطة</v-btn>
        <v-btn value="deleted" small>التصنيفات المحذوفة</v-btn>
      </v-btn-toggle>
    </div>

    <!-- Filters -->
    <v-card class="mb-4">
      <v-card-text>
        <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
          <span class="filter-label">المستخدم:</span>
          <v-btn-toggle
            v-model="filters.class_user"
            mandatory
            dense
            color="primary"
            @change="applyFilters"
          >
            <v-btn
              v-for="opt in roleFilterOptions"
              :key="`role-${opt.value}`"
              :value="opt.value"
              small
            >
              {{ opt.label }}
            </v-btn>
          </v-btn-toggle>
        </div>

        <div class="d-flex flex-wrap align-center" style="gap: 12px">
          <v-select
            v-model="filters.class_type"
            :items="typeFilterOptions"
            item-text="label"
            item-value="value"
            label="نوع التصنيف"
            dense
            outlined
            hide-details
            style="max-width: 200px"
            @change="applyFilters"
          />
          <v-text-field
            v-model="filters.class_name"
            label="البحث بالتصنيف"
            dense
            outlined
            hide-details
            clearable
            style="max-width: 280px"
            @input="onSearchInput"
            @click:clear="applyFilters"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- Table -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="classifications"
        :loading="loading"
        :server-items-length="total"
        :items-per-page="perPage"
        :page.sync="currentPage"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        item-key="id"
        class="elevation-1"
        @update:page="fetchClassifications"
        @update:items-per-page="onPerPageChange"
      >
        <!-- اللون -->
        <template v-slot:[`item.color`]="{ item }">
          <span
            class="color-cell-swatch"
            :style="{ backgroundColor: item.color || '#757575' }"
            :title="item.color || '#757575'"
          ></span>
        </template>

        <!-- التصنيف -->
        <template v-slot:[`item.value`]="{ item }">
          <span :class="{ 'deleted-classification-label': item.is_deleted }">{{ item.value }}</span>
        </template>

        <!-- نوعه (type): سلبي / إيجابي / محايد -->
        <template v-slot:[`item.type`]="{ item }">
          <v-chip
            :color="typeColor(item.type)"
            dark
            label
            small
            class="type-badge font-weight-medium"
            :disabled="busy[item.id] || listStatus === 'deleted'"
            @click="toggleType(item)"
          >
            <v-icon small class="type-badge__icon">
              {{ typeIcon(item.type) }}
            </v-icon>
            <span>{{ item.type_label }}</span>
          </v-chip>
        </template>

        <!-- متطلب للحسبة؟ -->
        <template v-slot:[`item.is_required_in_calculater`]="{ item }">
          <v-btn
            :color="item.is_required_in_calculater ? 'success' : 'error'"
            fab
            x-small
            dark
            depressed
            :loading="busy[item.id]"
            :disabled="listStatus === 'deleted'"
            @click="toggleFlag(item, 'is_required_in_calculater')"
          >
            <v-icon small>
              {{ item.is_required_in_calculater ? "mdi-check" : "mdi-close" }}
            </v-icon>
          </v-btn>
        </template>

        <!-- هل يحول لسلة التحويل؟ -->
        <template v-slot:[`item.is_transferred_to_need_action`]="{ item }">
          <v-btn
            :color="item.is_transferred_to_need_action ? 'success' : 'error'"
            fab
            x-small
            dark
            depressed
            :loading="busy[item.id]"
            @click="toggleFlag(item, 'is_transferred_to_need_action')"
          >
            <v-icon small>
              {{ item.is_transferred_to_need_action ? "mdi-check" : "mdi-close" }}
            </v-icon>
          </v-btn>
        </template>

        <!-- هل أوتوماتك؟ -->
        <template v-slot:[`item.is_automatic`]="{ item }">
          <v-btn
            :color="item.is_automatic === 'yes' ? 'success' : 'error'"
            fab
            x-small
            dark
            depressed
            :loading="busy[item.id]"
            @click="toggleFlag(item, 'is_automatic')"
          >
            <v-icon small>
              {{ item.is_automatic === "yes" ? "mdi-check" : "mdi-close" }}
            </v-icon>
          </v-btn>
        </template>

        <!-- يتطلب سبب رفض؟ -->
        <template v-slot:[`item.is_rejected`]="{ item }">
          <v-btn
            :color="item.is_rejected ? 'success' : 'error'"
            fab
            x-small
            dark
            depressed
            :loading="busy[item.id]"
            @click="toggleFlag(item, 'is_rejected')"
          >
            <v-icon small>
              {{ item.is_rejected ? "mdi-check" : "mdi-close" }}
            </v-icon>
          </v-btn>
        </template>

        <!-- العمليات -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-center actions-cell">
            <template v-if="listStatus === 'active'">
              <v-btn
                color="blue darken-1"
                fab
                x-small
                dark
                depressed
                class="action-btn"
                @click="openEditDialog(item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                color="error"
                fab
                x-small
                dark
                depressed
                class="action-btn"
                @click="openDeleteDialog(item)"
              >
                <v-icon small>mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <v-btn
              v-else
              color="success"
              fab
              x-small
              dark
              depressed
              class="action-btn"
              :loading="busy[item.id]"
              @click="restoreClassification(item)"
            >
              <v-icon small>mdi-restore</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add / Edit Dialog -->
    <ClassificationDialog
      v-model="dialog"
      :edit-id="editId"
      :roles="roles"
      @saved="fetchClassifications"
    />

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="420px" persistent>
      <v-card>
        <v-card-title class="headline">هل أنت متأكد من حذف التصنيف؟</v-card-title>
        <v-card-text>
          <!-- سيتم حذف التصنيف نهائياً وإزالته من جميع الطلبات المرتبطة به. -->
          سيتم إخفاء التصنيف من القوائم مع الإبقاء على سجل الطلبات والتاريخ. يمكن استرجاعه لاحقاً.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">
            تأكيد الحذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Axios from "axios";
import ClassificationDialog from "./ClassificationDialog.vue";

export default {
  name: "ClassificationSettingsIndex",
  components: { ClassificationDialog },
  data() {
    return {
      loading: false,
      listStatus: "active",
      classifications: [],
      total: 0,
      perPage: 10,
      currentPage: 1,
      roles: [],
      busy: {},
      filters: {
        class_user: "all",
        class_type: "all",
        class_name: "",
      },
      searchTimer: null,
      dialog: false,
      editId: null,
      deleteDialog: false,
      deleting: false,
      targetItem: null,
      roleFilterOptions: [
        { label: "الكل", value: "all" },
        { label: "الاستشاري", value: 0 },
        { label: "خدمة العملاء", value: "cs" },
        { label: "مدير المبيعات", value: 1 },
        { label: "مشرف التمويل", value: 2 },
        { label: "مدير الرهن", value: 3 },
        { label: "الجودة", value: 5 },
      ],
      typeFilterOptions: [
        { label: "الكل", value: "all" },
        { label: "إيجابي", value: 1 },
        { label: "سلبي", value: 0 },
        { label: "محايد", value: 2 },
      ],
      headers: [
        { text: "المستخدم", value: "user_role_label", sortable: false },
        { text: "التصنيف", value: "value", sortable: false },
        { text: "اللون", value: "color", sortable: false },
        { text: "نوعه", value: "type", sortable: false },
        { text: "متطلب للحسبة؟", value: "is_required_in_calculater", sortable: false },
        { text: "هل يحول لسلة التحويل؟", value: "is_transferred_to_need_action", sortable: false },
        { text: "هل أوتوماتك؟", value: "is_automatic", sortable: false },
        { text: "يتطلب سبب رفض؟", value: "is_rejected", sortable: false },
        { text: "العمليات", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    apiBase() {
      return `${this.$store.state.url}/employee/admin/classifications`;
    },
  },
  methods: {
    async fetchClassifications() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
          status: this.listStatus,
        };
        if (this.filters.class_user !== "all") {
          params.class_user = this.filters.class_user;
        }
        if (this.filters.class_type !== "all") {
          params.class_type = this.filters.class_type;
        }
        if (this.filters.class_name) {
          params.class_name = this.filters.class_name;
        }

        const res = await Axios.get(this.apiBase, {
          params,
          headers: this.request_headers(),
        });
        const payload = res.data.payload || {};
        this.classifications = payload.data || [];
        this.total = payload.count || 0;
        this.roles = payload.roles || [];
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchClassifications();
    },
    onListStatusChange() {
      this.currentPage = 1;
      this.fetchClassifications();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applyFilters(), 500);
    },
    onPerPageChange(v) {
      this.perPage = v;
      this.currentPage = 1;
      this.fetchClassifications();
    },
    setBusy(id, val) {
      this.$set(this.busy, id, val);
    },
    typeLabel(type) {
      if (type === 1) return "إيجابي";
      if (type === 2) return "محايد";
      return "سلبي";
    },
    typeColor(type) {
      if (type === 1) return "success";
      if (type === 2) return "grey";
      return "error";
    },
    typeIcon(type) {
      if (type === 1) return "mdi-check-circle";
      if (type === 2) return "mdi-minus-circle";
      return "mdi-close-circle";
    },
    async toggleType(item) {
      const prev = item.type;
      // Cycle سلبي (0) → إيجابي (1) → محايد (2) → سلبي (0)
      item.type = (item.type + 1) % 3;
      item.type_label = this.typeLabel(item.type);
      this.setBusy(item.id, true);
      try {
        await Axios.post(
          `${this.apiBase}/${item.id}/toggle-type`,
          {},
          { headers: this.request_headers() }
        );
      } catch (e) {
        item.type = prev;
        item.type_label = this.typeLabel(prev);
        this.showPopup("error", e.response?.data?.message || "تعذر تغيير النوع");
      } finally {
        this.setBusy(item.id, false);
      }
    },
    async toggleFlag(item, field) {
      const prev = item[field];
      if (field === "is_automatic") {
        item[field] = item[field] === "yes" ? "no" : "yes";
      } else {
        item[field] = !item[field];
      }
      this.setBusy(item.id, true);
      try {
        const body = new FormData();
        body.append("field", field);
        await Axios.post(`${this.apiBase}/${item.id}/toggle-flag`, body, {
          headers: this.request_headers(),
        });
      } catch (e) {
        item[field] = prev;
        this.showPopup("error", e.response?.data?.message || "تعذر التحديث");
      } finally {
        this.setBusy(item.id, false);
      }
    },
    openAddDialog() {
      this.editId = null;
      this.dialog = true;
    },
    openEditDialog(item) {
      this.editId = item.id;
      this.dialog = true;
    },
    openDeleteDialog(item) {
      this.targetItem = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleting = true;
      try {
        await Axios.post(`${this.apiBase}/delete/${this.targetItem.id}`,{}, {
          headers: this.request_headers(),
        });
        this.showPopup("success", "تم حذف التصنيف بنجاح");
        this.deleteDialog = false;
        if (this.classifications.length === 1 && this.currentPage > 1) {
          this.currentPage -= 1;
        }
        this.fetchClassifications();
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ");
      } finally {
        this.deleting = false;
      }
    },
    async restoreClassification(item) {
      this.setBusy(item.id, true);
      try {
        await Axios.post(`${this.apiBase}/restore/${item.id}`, {}, {
          headers: this.request_headers(),
        });
        this.showPopup("success", "تم استرجاع التصنيف بنجاح");
        this.fetchClassifications();
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "تعذر استرجاع التصنيف");
      } finally {
        this.setBusy(item.id, false);
      }
    },
  },
  created() {
    this.fetchClassifications();
  },
};
</script>

<style scoped>
.classifications-settings {
  padding: 16px;
}
.filter-label {
  font-weight: 600;
}

.deleted-classification-label {
  color: #c62828;
  font-weight: 600;
}

/* color swatch cell in the list */
.color-cell-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid rgba(140, 140, 140, 0.4);
  vertical-align: middle;
}

/* نوعه badge: space between icon and label */
.type-badge {
  cursor: pointer;
}
.type-badge__icon {
  margin-inline-end: 6px;
}

/* spacing between edit and delete action buttons */
.actions-cell {
  gap: 10px;
}
.action-btn {
  margin: 0;
}

/* vertical separators between table columns */
.classifications-settings >>> .v-data-table td:not(:last-child),
.classifications-settings >>> .v-data-table th:not(:last-child) {
  border-inline-start: 1px solid rgba(140, 140, 140, 0.22);
}

/* shrink the circular toggle / action icon badges */
.classifications-settings >>> .v-data-table td .v-btn--fab.v-size--x-small {
  height: 24px;
  width: 24px;
}
.classifications-settings >>> .v-data-table td .v-btn--fab.v-size--x-small .v-icon {
  font-size: 15px;
}
</style>
